<template>
  <v-dialog
    :value="value"
    max-width="900"
    persistent
    scrollable
    :retain-focus="false"
    @input="$emit('input', $event)"
  >
    <v-card dark>
      <v-card-title>
        {{ canEdit ? "Manage Permissions" : "View Permissions" }}
        <v-spacer />
        <v-select
          v-model="localYearFilter"
          :items="yearOptions"
          label="Filter by year"
          dense
          outlined
          hide-details
          clearable
          style="max-width: 160px"
          :menu-props="menuProps"
        />
      </v-card-title>
      <v-card-text>
        <div v-if="loading" class="d-flex align-center justify-center py-8">
          <v-progress-circular indeterminate color="secondary" class="mr-3" />
          <span>Loading permission details…</span>
        </div>
        <div v-else-if="loadError" class="py-4 error--text">
          {{ loadError }}
        </div>
        <div v-else-if="!filteredEvents.length" class="caption grey--text py-4">
          No permissions
          {{ localYearFilter ? `for ${localYearFilter}` : "" }}.
        </div>
        <template v-else>
          <v-card
            v-for="ev in filteredEvents"
            :key="ev.EventId"
            class="mb-3"
            outlined
            dark
          >
            <v-card-title class="subtitle-2 py-2">
              {{ eventLabel(ev.EventId) }}
              <v-spacer />
              <v-select
                v-if="canEdit"
                :value="ev.Permission"
                :items="permissionOptions"
                dense
                hide-details
                style="max-width: 140px"
                class="mr-2"
                :menu-props="menuProps"
                @change="setEventPermission(ev.EventId, $event)"
              />
              <span v-else class="mr-2">{{ ev.Permission }}</span>
              <v-btn
                v-if="canEdit"
                icon
                small
                @click="removeEvent(ev.EventId)"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text
              v-if="canEdit || (ev.Stallholders && ev.Stallholders.length)"
            >
              <div
                v-for="sh in ev.Stallholders || []"
                :key="ev.EventId + '-' + sh.StallholderId"
                class="mb-2 ml-2"
              >
                <div class="d-flex align-center">
                  <span class="mr-2">{{
                    stallholderLabel(ev.EventId, sh.StallholderId)
                  }}</span>
                  <v-select
                    v-if="canEdit"
                    :value="sh.Permission"
                    :items="permissionOptions"
                    dense
                    hide-details
                    style="max-width: 120px"
                    class="mr-2"
                    :menu-props="menuProps"
                    @change="
                      setStallholderPermission(
                        ev.EventId,
                        sh.StallholderId,
                        $event
                      )
                    "
                  />
                  <span v-else class="mr-2">{{ sh.Permission }}</span>
                  <v-btn
                    v-if="canEdit"
                    icon
                    x-small
                    @click="removeStallholder(ev.EventId, sh.StallholderId)"
                  >
                    <v-icon x-small>mdi-close</v-icon>
                  </v-btn>
                </div>
                <div
                  v-for="stall in sh.Stalls || []"
                  :key="sh.StallholderId + '-' + stall.StallId"
                  class="d-flex align-center ml-4 mt-1"
                >
                  <span class="mr-2">{{
                    stallLabel(ev.EventId, sh.StallholderId, stall.StallId)
                  }}</span>
                  <v-select
                    v-if="canEdit"
                    :value="stall.Permission"
                    :items="permissionOptions"
                    dense
                    hide-details
                    style="max-width: 120px"
                    class="mr-2"
                    :menu-props="menuProps"
                    @change="
                      setStallPermission(
                        ev.EventId,
                        sh.StallholderId,
                        stall.StallId,
                        $event
                      )
                    "
                  />
                  <span v-else>{{ stall.Permission }}</span>
                  <v-btn
                    v-if="canEdit"
                    icon
                    x-small
                    @click="
                      removeStall(ev.EventId, sh.StallholderId, stall.StallId)
                    "
                  >
                    <v-icon x-small>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close">{{ canEdit ? "Done" : "Close" }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { dashboardAPI } from "@/services";

const PERMS = ["Write", "Read", "Stock", "None"];

export default {
  name: "ManagePermissionsDialog",
  props: {
    value: { type: Boolean, default: false },
    events: { type: Array, default: () => [] },
    canEdit: { type: Boolean, default: true },
    /** Shared year filter from parent (e.g. user edit); null = all years */
    yearFilter: { type: [Number, String], default: null },
  },
  data() {
    const y = new Date().getFullYear();
    return {
      yearOptions: [y + 1, y, y - 1, y - 2, y - 3, y - 4, y - 5],
      permissionOptions: PERMS,
      menuProps: { zIndex: 3000, offsetY: true, maxHeight: 280 },
      localYearFilter: y,
      eventMeta: {},
      loading: false,
      loadError: "",
    };
  },
  computed: {
    filteredEvents() {
      const list = [...(this.events || [])];
      list.sort((a, b) => {
        const sa = String(this.eventMeta[a.EventId]?.startDateTime || "");
        const sb = String(this.eventMeta[b.EventId]?.startDateTime || "");
        return sb.localeCompare(sa);
      });
      if (this.localYearFilter == null || this.localYearFilter === "") {
        return list;
      }
      return list.filter((ev) => {
        const year = this.eventMeta[ev.EventId]?.Year;
        return String(year) === String(this.localYearFilter);
      });
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.localYearFilter = this.normalizeYear(
          this.yearFilter != null && this.yearFilter !== ""
            ? this.yearFilter
            : new Date().getFullYear()
        );
        this.loadResolve();
      }
    },
    yearFilter(y) {
      if (this.value) this.localYearFilter = this.normalizeYear(y);
    },
  },
  methods: {
    normalizeYear(y) {
      if (y == null || y === "") return null;
      const n = Number(y);
      return Number.isFinite(n) ? n : null;
    },
    close() {
      this.$emit("input", false);
    },
    emitEvents(next) {
      this.$emit("update:events", next);
    },
    findEvent(eventId) {
      return (this.events || []).find((e) => e.EventId === eventId);
    },
    async loadResolve() {
      const events = this.events || [];
      const ids = events.map((e) => e.EventId).filter(Boolean);
      this.loadError = "";
      if (!ids.length) {
        this.eventMeta = {};
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        // Labels-only (no full catalogs) — works for 200+ event ACLs
        const data = await dashboardAPI
          .getDashboardAccessAPI()
          .resolvePermissions({ EventIds: ids, Events: events });
        this.eventMeta = data.Events || {};
      } catch (e) {
        this.eventMeta = {};
        this.loadError = "Failed to load permission details. Try again.";
      } finally {
        this.loading = false;
      }
    },
    eventLabel(eventId) {
      const m = this.eventMeta[eventId];
      if (m?.EventName) return `${m.EventName} (${eventId})`;
      return eventId;
    },
    stallholderLabel(eventId, shId) {
      const sh = (this.eventMeta[eventId]?.Stallholders || []).find(
        (s) => s.StallholderId === shId
      );
      return sh?.StallholderName || shId;
    },
    stallLabel(eventId, shId, stallId) {
      const sh = (this.eventMeta[eventId]?.Stallholders || []).find(
        (s) => s.StallholderId === shId
      );
      const stall = (sh?.Stalls || []).find((s) => s.StallId === stallId);
      return stall?.StallName || stallId;
    },
    setEventPermission(eventId, permission) {
      const next = (this.events || []).map((ev) =>
        ev.EventId === eventId ? { ...ev, Permission: permission } : ev
      );
      this.emitEvents(next);
    },
    removeEvent(eventId) {
      this.emitEvents((this.events || []).filter((e) => e.EventId !== eventId));
    },
    setStallholderPermission(eventId, shId, permission) {
      const next = (this.events || []).map((ev) => {
        if (ev.EventId !== eventId) return ev;
        return {
          ...ev,
          Stallholders: (ev.Stallholders || []).map((sh) =>
            sh.StallholderId === shId ? { ...sh, Permission: permission } : sh
          ),
        };
      });
      this.emitEvents(next);
    },
    removeStallholder(eventId, shId) {
      const next = (this.events || []).map((ev) => {
        if (ev.EventId !== eventId) return ev;
        return {
          ...ev,
          Stallholders: (ev.Stallholders || []).filter(
            (sh) => sh.StallholderId !== shId
          ),
        };
      });
      this.emitEvents(next);
    },
    setStallPermission(eventId, shId, stallId, permission) {
      const next = (this.events || []).map((ev) => {
        if (ev.EventId !== eventId) return ev;
        return {
          ...ev,
          Stallholders: (ev.Stallholders || []).map((sh) => {
            if (sh.StallholderId !== shId) return sh;
            return {
              ...sh,
              Stalls: (sh.Stalls || []).map((st) =>
                st.StallId === stallId ? { ...st, Permission: permission } : st
              ),
            };
          }),
        };
      });
      this.emitEvents(next);
    },
    removeStall(eventId, shId, stallId) {
      const next = (this.events || []).map((ev) => {
        if (ev.EventId !== eventId) return ev;
        return {
          ...ev,
          Stallholders: (ev.Stallholders || []).map((sh) => {
            if (sh.StallholderId !== shId) return sh;
            return {
              ...sh,
              Stalls: (sh.Stalls || []).filter((st) => st.StallId !== stallId),
            };
          }),
        };
      });
      this.emitEvents(next);
    },
  },
};
</script>
